import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { levantamentoAPI, sinapiAPI, aiAPI } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Plus, Trash2, Loader2, Save, List, Layers, GitBranch, ChevronRight, ChevronDown, Lightbulb, PackagePlus, Sparkles, DollarSign, Search, Database } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ComposicaoTree } from './ComposicaoTree'

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

interface ManualCalculatorProps {
  projectId: string
  levantamentoId: string
  itens: any[]
  ambienteId?: string
  etapas?: string[]
  activityGroups?: any
  fixedActivityId?: string
  fixedActivityName?: string
  childActivities?: any[]
  baseQuantity?: number
}

interface EditingItem {
  id?: string
  nome: string
  unidade: string
  quantidade: string
  precoUnitario: string
  etapa: string
}

const emptyItem: EditingItem = { nome: '', unidade: 'UN', quantidade: '', precoUnitario: '', etapa: '' }

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

function SinapiPricePopover({ itemName, onSelect, uf, mesReferencia }: {
  itemName: string
  onSelect: (preco: number, insumoId: string) => void
  uf: string
  mesReferencia: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(itemName)

  // Reset search to item name when popover opens
  useEffect(() => {
    if (open) setSearch(itemName)
  }, [open, itemName])

  const debouncedSearch = useDebounce(search, 400)

  const { data: results, isLoading } = useQuery({
    queryKey: ['sinapi-insumo-semantic', debouncedSearch, uf, mesReferencia],
    queryFn: () => sinapiAPI.searchSemanticInsumos({
      query: debouncedSearch,
      limit: 8,
      uf,
      mesReferencia,
    }),
    enabled: open && debouncedSearch.length >= 2 && !!mesReferencia,
    staleTime: 30_000,
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-emerald-500 hover:text-emerald-700"
          title="Buscar preço SINAPI"
        >
          <DollarSign className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" side="bottom">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
            <Input
              className="h-8 text-xs pl-8"
              placeholder="Buscar insumo SINAPI..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {!mesReferencia && (
            <p className="text-[10px] text-amber-600 mt-1.5">Selecione o mês SINAPI na aba Detalhada para ver preços.</p>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Buscando...
            </div>
          ) : results && results.length > 0 ? (
            <div className="divide-y">
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors"
                  onClick={() => {
                    if (r.preco != null && r.preco > 0) {
                      onSelect(r.preco, r.id)
                      setOpen(false)
                    } else {
                      toast.warning('Insumo sem preço para UF/mês selecionado')
                    }
                  }}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] text-blue-500 font-mono mt-0.5 shrink-0">{r.codigo}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-neutral-800 leading-snug line-clamp-2">{r.descricao}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-neutral-400">{r.unidade}</span>
                        {r.preco != null && r.preco > 0 ? (
                          <span className="text-[11px] font-semibold text-emerald-600">
                            {formatCurrency(r.preco)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-neutral-400 italic">sem preço</span>
                        )}
                        <span className="text-[10px] text-neutral-300 ml-auto">
                          {Math.round(r.similarity * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : debouncedSearch.length >= 2 && !isLoading ? (
            <p className="text-xs text-neutral-400 text-center py-6">Nenhum insumo encontrado.</p>
          ) : (
            <p className="text-xs text-neutral-400 text-center py-6">Digite para buscar...</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function SinapiLookupPopover({ itemName, onSelect, uf, mesReferencia }: {
  itemName: string
  onSelect: (data: { nome: string; preco: number; sinapiInsumoId: string; codigo: string }) => void
  uf: string
  mesReferencia: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(itemName)

  useEffect(() => {
    if (open) setSearch(itemName)
  }, [open, itemName])

  const debouncedSearch = useDebounce(search, 400)

  const { data: results, isLoading } = useQuery({
    queryKey: ['sinapi-insumo-lookup', debouncedSearch, uf, mesReferencia],
    queryFn: () => sinapiAPI.searchSemanticInsumos({
      query: debouncedSearch,
      limit: 8,
      uf,
      mesReferencia,
    }),
    enabled: open && debouncedSearch.length >= 2 && !!mesReferencia,
    staleTime: 30_000,
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700"
          title="Vincular insumo SINAPI (atualiza nome + preço)"
        >
          <Database className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" side="bottom">
        <div className="p-3 border-b">
          <p className="text-[10px] font-medium text-blue-600 mb-1.5">Vincular insumo SINAPI — atualiza nome e preço</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
            <Input
              className="h-8 text-xs pl-8"
              placeholder="Buscar insumo SINAPI..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {!mesReferencia && (
            <p className="text-[10px] text-amber-600 mt-1.5">Selecione o mês SINAPI na aba Detalhada para ver preços.</p>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Buscando...
            </div>
          ) : results && results.length > 0 ? (
            <div className="divide-y">
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors"
                  onClick={() => {
                    const preco = (r.preco != null && r.preco > 0) ? r.preco : 0
                    onSelect({
                      nome: r.descricao,
                      preco,
                      sinapiInsumoId: r.id,
                      codigo: r.codigo,
                    })
                    setOpen(false)
                    toast.success(`Vinculado: SINAPI ${r.codigo}`)
                  }}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] text-blue-500 font-mono mt-0.5 shrink-0">{r.codigo}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-neutral-800 leading-snug line-clamp-2">{r.descricao}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-neutral-400">{r.unidade}</span>
                        {r.preco != null && r.preco > 0 ? (
                          <span className="text-[11px] font-semibold text-emerald-600">
                            {formatCurrency(r.preco)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-neutral-400 italic">sem preço</span>
                        )}
                        {r.similarity > 0 && (
                          <span className="text-[10px] text-neutral-300 ml-auto">
                            {Math.round(r.similarity * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : debouncedSearch.length >= 2 && !isLoading ? (
            <p className="text-xs text-neutral-400 text-center py-6">Nenhum insumo encontrado.</p>
          ) : (
            <p className="text-xs text-neutral-400 text-center py-6">Digite para buscar...</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function ManualCalculator({ projectId, levantamentoId, itens, ambienteId, etapas = [], activityGroups, fixedActivityId, fixedActivityName, childActivities, baseQuantity = 0 }: ManualCalculatorProps) {
  const queryClient = useQueryClient()

  // Derive dropdown options from activityGroups (STAGE names) or fallback to etapas
  // value = "activityId::name" when activity exists, or plain "name" for fallback
  const etapaOptions = useMemo(() => {
    if (activityGroups?.activityGroups?.length > 0) {
      return activityGroups.activityGroups.map((g: any) => ({
        value: `${g.activity.id}::${g.activity.name}`,
        label: g.activity.parentName ? `${g.activity.parentName} > ${g.activity.name}` : g.activity.name,
      }))
    }
    return etapas.map((e: string) => ({ value: e, label: e }))
  }, [activityGroups, etapas])

  // Parse etapa dropdown value into { etapa, projectActivityId }
  const parseEtapaValue = (v: string): { etapa: string; projectActivityId: string | null } => {
    if (v === '_none' || !v) return { etapa: '', projectActivityId: null }
    const sep = v.indexOf('::')
    if (sep > 0) {
      return { etapa: v.substring(sep + 2), projectActivityId: v.substring(0, sep) }
    }
    return { etapa: v, projectActivityId: null }
  }

  // Build the reverse: from etapa string or projectActivityId -> dropdown value
  const toEtapaValue = (etapa: string, projectActivityId?: string | null): string => {
    if (!etapa && !projectActivityId) return '_none'
    if (projectActivityId) {
      const found = etapaOptions.find((o: any) => o.value.startsWith(projectActivityId + '::'))
      if (found) return found.value
    }
    // Try match by name
    const found = etapaOptions.find((o: any) => {
      const parsed = parseEtapaValue(o.value)
      return parsed.etapa === etapa
    })
    return found?.value || etapa || '_none'
  }

  const [viewMode, setViewMode] = useState<'simple' | 'grouped' | 'detailed'>('simple')
  const [newItem, setNewItem] = useState<EditingItem>({ ...emptyItem })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingData, setEditingData] = useState<EditingItem>({ ...emptyItem })

  // Group items by etapa for "completa" view
  const groupedItens = useMemo(() => {
    const groups: { key: string; label: string; items: any[]; total: number }[] = []
    const map = new Map<string, any[]>()

    for (const item of itens) {
      const key = item.etapa || '_sem_etapa'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    }

    for (const [key, items] of map) {
      const total = items.reduce((s: number, i: any) => s + Number(i.quantidade) * Number(i.precoUnitario), 0)
      groups.push({ key, label: key === '_sem_etapa' ? 'Sem etapa' : key, items, total })
    }

    return groups
  }, [itens])

  // SINAPI params for detailed view
  const [sinapiUf, setSinapiUf] = useState('SP')
  const [sinapiMes, setSinapiMes] = useState('')
  const [expandedTreeId, setExpandedTreeId] = useState<string | null>(null)
  const [treeCache, setTreeCache] = useState<Record<string, any>>({})
  const [treeLoading, setTreeLoading] = useState<string | null>(null)

  // Fetch available SINAPI months (always active for price popover and detailed view)
  const { data: mesesDisponiveis } = useQuery({
    queryKey: ['sinapi-meses'],
    queryFn: () => sinapiAPI.getMesesReferencia(),
    staleTime: 10 * 60 * 1000,
  })

  // Auto-select most recent month
  useEffect(() => {
    if (mesesDisponiveis && mesesDisponiveis.length > 0 && !sinapiMes) {
      setSinapiMes(mesesDisponiveis[0])
    }
  }, [mesesDisponiveis, sinapiMes])

  // Collect unique composicaoIds from items
  const composicaoIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of itens) {
      if (item.sinapiComposicaoId) ids.add(item.sinapiComposicaoId)
    }
    return ids
  }, [itens])

  const hasComposicoes = composicaoIds.size > 0

  // Fetch tree for a specific composicao
  const fetchTree = async (composicaoId: string) => {
    if (treeCache[composicaoId]) return
    if (!sinapiMes) {
      toast.warning('Aguarde o carregamento dos meses SINAPI')
      return
    }
    setTreeLoading(composicaoId)
    try {
      const tree = await sinapiAPI.getComposicaoTree(composicaoId, {
        uf: sinapiUf,
        mesReferencia: sinapiMes,
      })
      setTreeCache((prev) => ({ ...prev, [composicaoId]: tree }))
    } catch {
      toast.error('Erro ao carregar composição SINAPI')
    } finally {
      setTreeLoading(null)
    }
  }

  const toggleTree = (itemId: string, composicaoId: string) => {
    if (expandedTreeId === itemId) {
      setExpandedTreeId(null)
    } else {
      setExpandedTreeId(itemId)
      fetchTree(composicaoId)
    }
  }

  const addMutation = useMutation({
    mutationFn: (data: any) => levantamentoAPI.addItem(projectId, levantamentoId, data),
    onSuccess: () => {
      toast.success('Item adicionado')
      queryClient.invalidateQueries({ queryKey: ['levantamento-project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['workflow-check', 'levantamento-items'] })
      setNewItem({ ...emptyItem })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: any }) =>
      levantamentoAPI.updateItem(projectId, levantamentoId, itemId, data),
    onSuccess: () => {
      toast.success('Item atualizado')
      queryClient.invalidateQueries({ queryKey: ['levantamento-project', projectId] })
      setEditingId(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => levantamentoAPI.deleteItem(projectId, levantamentoId, itemId),
    onSuccess: () => {
      toast.success('Item removido')
      queryClient.invalidateQueries({ queryKey: ['levantamento-project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['workflow-check', 'levantamento-items'] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleAdd = () => {
    const qtd = parseFloat(newItem.quantidade)
    const preco = parseFloat(newItem.precoUnitario)
    if (!newItem.nome || isNaN(qtd) || isNaN(preco)) {
      toast.error('Preencha nome, quantidade e preço')
      return
    }
    if (fixedActivityId) {
      addMutation.mutate({
        nome: newItem.nome,
        unidade: newItem.unidade || 'UN',
        quantidade: qtd,
        precoUnitario: preco,
        etapa: fixedActivityName || undefined,
        projectActivityId: fixedActivityId,
        ambienteId: ambienteId || undefined,
      })
    } else {
      const parsed = parseEtapaValue(newItem.etapa)
      addMutation.mutate({
        nome: newItem.nome,
        unidade: newItem.unidade || 'UN',
        quantidade: qtd,
        precoUnitario: preco,
        etapa: parsed.etapa || undefined,
        projectActivityId: parsed.projectActivityId || undefined,
        ambienteId: ambienteId || undefined,
      })
    }
  }

  const handleSaveEdit = () => {
    if (!editingId) return
    const qtd = parseFloat(editingData.quantidade)
    const preco = parseFloat(editingData.precoUnitario)
    if (!editingData.nome || isNaN(qtd) || isNaN(preco)) {
      toast.error('Preencha nome, quantidade e preço')
      return
    }
    if (fixedActivityId) {
      updateMutation.mutate({
        itemId: editingId,
        data: {
          nome: editingData.nome,
          unidade: editingData.unidade,
          quantidade: qtd,
          precoUnitario: preco,
          etapa: fixedActivityName || null,
          projectActivityId: fixedActivityId,
        },
      })
    } else {
      const parsed = parseEtapaValue(editingData.etapa)
      updateMutation.mutate({
        itemId: editingId,
        data: {
          nome: editingData.nome,
          unidade: editingData.unidade,
          quantidade: qtd,
          precoUnitario: preco,
          etapa: parsed.etapa || null,
          projectActivityId: parsed.projectActivityId || null,
        },
      })
    }
  }

  const startEdit = (item: any) => {
    setEditingId(item.id)
    setEditingData({
      id: item.id,
      nome: item.nome,
      unidade: item.unidade,
      quantidade: String(item.quantidade),
      precoUnitario: String(item.precoUnitario),
      etapa: toEtapaValue(item.etapa || '', item.projectActivityId),
    })
  }

  const totalGeral = itens.reduce(
    (sum, item) => sum + Number(item.quantidade) * Number(item.precoUnitario),
    0,
  )

  const renderItemRow = (item: any) => {
    const isEditing = editingId === item.id
    const total = Number(item.quantidade) * Number(item.precoUnitario)

    if (isEditing) {
      return (
        <TableRow key={item.id}>
          <TableCell><Input className="h-7 text-xs" value={editingData.nome} onChange={(e) => setEditingData({ ...editingData, nome: e.target.value })} /></TableCell>
          <TableCell><Input className="h-7 text-xs" value={editingData.unidade} onChange={(e) => setEditingData({ ...editingData, unidade: e.target.value })} /></TableCell>
          <TableCell><Input className="h-7 text-xs text-right" type="number" value={editingData.quantidade} onChange={(e) => setEditingData({ ...editingData, quantidade: e.target.value })} /></TableCell>
          <TableCell><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editingData.precoUnitario} onChange={(e) => setEditingData({ ...editingData, precoUnitario: e.target.value })} /></TableCell>
          <TableCell className="text-xs text-right font-medium">-</TableCell>
          {viewMode === 'simple' && !fixedActivityId && (
            <TableCell>
              <Select value={editingData.etapa || '_none'} onValueChange={(v) => setEditingData({ ...editingData, etapa: v === '_none' ? '' : v })}>
                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {etapaOptions.map((opt: any) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TableCell>
          )}
          <TableCell>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-neutral-400" onClick={() => setEditingId(null)}>
                ✕
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )
    }

    return (
      <TableRow key={item.id} className="cursor-pointer hover:bg-neutral-50" onDoubleClick={() => startEdit(item)}>
        <TableCell className="text-xs">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span>{item.nome}</span>
            {(item.sinapiInsumo?.codigo || item.sinapiInsumoId) && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-blue-300 text-blue-600 font-mono">
                SINAPI {item.sinapiInsumo?.codigo || ''}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-xs">{item.unidade}</TableCell>
        <TableCell className="text-xs text-right font-mono">{Number(item.quantidade).toFixed(2)}</TableCell>
        <TableCell className="text-xs text-right font-mono">{formatCurrency(Number(item.precoUnitario))}</TableCell>
        <TableCell className="text-xs text-right font-mono font-medium">{formatCurrency(total)}</TableCell>
        {viewMode === 'simple' && !fixedActivityId && (
          <TableCell className="text-xs text-neutral-500">{item.etapa || '-'}</TableCell>
        )}
        <TableCell>
          <div className="flex gap-0.5">
            <SinapiPricePopover
              itemName={item.nome}
              uf={sinapiUf}
              mesReferencia={sinapiMes}
              onSelect={(preco, insumoId) => {
                updateMutation.mutate({
                  itemId: item.id,
                  data: { precoUnitario: preco, sinapiInsumoId: insumoId },
                })
              }}
            />
            <SinapiLookupPopover
              itemName={item.nome}
              uf={sinapiUf}
              mesReferencia={sinapiMes}
              onSelect={({ nome, preco, sinapiInsumoId }) => {
                updateMutation.mutate({
                  itemId: item.id,
                  data: { nome, precoUnitario: preco, sinapiInsumoId },
                })
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
              onClick={() => {
                if (confirm('Remover este item?')) deleteMutation.mutate(item.id)
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    )
  }

  const renderNewItemRow = () => (
    <TableRow className="bg-neutral-50/50">
      <TableCell>
        <Input
          className="h-7 text-xs"
          placeholder="Nome do material/serviço"
          value={newItem.nome}
          onChange={(e) => setNewItem({ ...newItem, nome: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
      </TableCell>
      <TableCell>
        <Input className="h-7 text-xs" placeholder="UN" value={newItem.unidade} onChange={(e) => setNewItem({ ...newItem, unidade: e.target.value })} />
      </TableCell>
      <TableCell>
        <Input className="h-7 text-xs text-right" type="number" placeholder="0" value={newItem.quantidade} onChange={(e) => setNewItem({ ...newItem, quantidade: e.target.value })} />
      </TableCell>
      <TableCell>
        <Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="0,00" value={newItem.precoUnitario} onChange={(e) => setNewItem({ ...newItem, precoUnitario: e.target.value })} />
      </TableCell>
      <TableCell />
      {viewMode === 'simple' && !fixedActivityId && (
        <TableCell>
          <Select value={newItem.etapa || '_none'} onValueChange={(v) => setNewItem({ ...newItem, etapa: v === '_none' ? '' : v })}>
            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Etapa" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">—</SelectItem>
              {etapaOptions.map((opt: any) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
      )}
      <TableCell>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={handleAdd} disabled={addMutation.isPending}>
          {addMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
        </Button>
      </TableCell>
    </TableRow>
  )

  // === SINAPI Suggestions ===
  const childSinapiCodigos = useMemo(() => {
    if (!childActivities?.length) return []
    return childActivities
      .filter((c: any) => c.level === 'ACTIVITY' && c.sinapiCodigo)
      .map((c: any) => ({ name: c.name, sinapiCodigo: c.sinapiCodigo, id: c.id }))
  }, [childActivities])

  const childrenWithoutSinapi = useMemo(() => {
    if (!childActivities?.length) return []
    return childActivities.filter((c: any) => c.level === 'ACTIVITY' && !c.sinapiCodigo)
  }, [childActivities])

  // Fetch composition details (with insumos) for each child SINAPI code
  const { data: suggestionsData, isLoading: suggestionsLoading } = useQuery({
    queryKey: ['sinapi-suggestions', childSinapiCodigos.map(c => c.sinapiCodigo).join(',')],
    queryFn: async () => {
      // Step 1: find composition IDs by code
      const resolvedComps = await Promise.all(
        childSinapiCodigos.map(async (item) => {
          try {
            const res = await sinapiAPI.searchComposicoes({ search: item.sinapiCodigo, limit: 5 })
            const data = (res as any).data ?? (res as any).composicoes ?? res
            const list = Array.isArray(data) ? data : []
            const match = list.find((c: any) => c.codigo === item.sinapiCodigo) || list[0]
            return match ? { ...item, composicaoId: match.id, composicaoDesc: match.descricao } : null
          } catch {
            return null
          }
        })
      )
      const valid = resolvedComps.filter(Boolean) as { name: string; sinapiCodigo: string; id: string; composicaoId: string; composicaoDesc: string }[]

      // Step 2: fetch full composition details (with insumos) for each
      const details = await Promise.all(
        valid.map(async (comp) => {
          try {
            const detail = await sinapiAPI.getComposicao(comp.composicaoId)
            return {
              activityName: comp.name,
              sinapiCodigo: comp.sinapiCodigo,
              composicaoDesc: comp.composicaoDesc,
              itens: (detail.itens || []).map((item: any) => ({
                insumoId: item.insumo?.id || item.insumoId,
                codigo: item.insumo?.codigo || item.codigo,
                descricao: item.insumo?.descricao || item.descricao,
                unidade: item.insumo?.unidade || item.unidade || 'UN',
                tipo: item.insumo?.tipo || item.tipo,
                coeficiente: Number(item.coeficiente) || 0,
              })),
            }
          } catch {
            return null
          }
        })
      )
      return details.filter(Boolean)
    },
    enabled: childSinapiCodigos.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  // Suggestions state: track selected items and user-adjusted values
  interface SuggestionItem {
    key: string
    selected: boolean
    descricao: string
    unidade: string
    quantidade: string
    precoUnitario: string
    activityName: string
    sinapiCodigo: string
    insumoId?: string
  }

  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([])
  const [suggestionsInitialized, setSuggestionsInitialized] = useState(false)
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true)

  // Initialize suggestions when data loads
  useEffect(() => {
    if (!suggestionsData?.length || suggestionsInitialized) return
    const items: SuggestionItem[] = []
    for (const comp of suggestionsData) {
      if (!comp) continue
      for (const insumo of comp.itens) {
        items.push({
          key: `${comp.sinapiCodigo}_${insumo.codigo}`,
          selected: false,
          descricao: insumo.descricao,
          unidade: insumo.unidade,
          quantidade: insumo.coeficiente > 0 ? String(insumo.coeficiente) : '',
          precoUnitario: '',
          activityName: comp.activityName,
          sinapiCodigo: comp.sinapiCodigo,
          insumoId: insumo.insumoId,
        })
      }
    }
    setSuggestions(items)
    setSuggestionsInitialized(true)
  }, [suggestionsData, suggestionsInitialized])

  // Reset suggestions when child activities change
  useEffect(() => {
    setSuggestionsInitialized(false)
    setSuggestions([])
  }, [childSinapiCodigos.map(c => c.sinapiCodigo).join(',')])

  // Reset AI suggestions when the selected activity changes
  useEffect(() => {
    setAiSuggestions([])
    setAiSuggestionsError(null)
    setAiContext('')
  }, [fixedActivityId])

  const toggleSuggestion = (key: string) => {
    setSuggestions(prev => prev.map(s => s.key === key ? { ...s, selected: !s.selected } : s))
  }

  const toggleAllSuggestions = (selected: boolean) => {
    setSuggestions(prev => prev.map(s => ({ ...s, selected })))
  }

  const updateSuggestionField = (key: string, field: 'quantidade' | 'precoUnitario', value: string) => {
    setSuggestions(prev => prev.map(s => s.key === key ? { ...s, [field]: value } : s))
  }

  const selectedSuggestions = suggestions.filter(s => s.selected)

  const addSuggestionsMutation = useMutation({
    mutationFn: async (items: SuggestionItem[]) => {
      const results = []
      for (const item of items) {
        const qtd = parseFloat(item.quantidade)
        const preco = parseFloat(item.precoUnitario)
        if (!item.descricao || isNaN(qtd) || qtd <= 0) continue
        results.push(
          levantamentoAPI.addItem(projectId, levantamentoId, {
            nome: item.descricao,
            unidade: item.unidade,
            quantidade: qtd,
            precoUnitario: isNaN(preco) ? 0 : preco,
            sinapiInsumoId: item.insumoId || undefined,
            projectActivityId: fixedActivityId || undefined,
            etapa: fixedActivityName || undefined,
            ambienteId: ambienteId || undefined,
          })
        )
      }
      return Promise.all(results)
    },
    onSuccess: (results) => {
      toast.success(`${results.length} ite${results.length === 1 ? 'm adicionado' : 'ns adicionados'}`)
      queryClient.invalidateQueries({ queryKey: ['levantamento-project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['workflow-check', 'levantamento-items'] })
      // Uncheck added items
      const addedKeys = new Set(selectedSuggestions.map(s => s.key))
      setSuggestions(prev => prev.map(s => addedKeys.has(s.key) ? { ...s, selected: false } : s))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleAddSuggestions = () => {
    const valid = selectedSuggestions.filter(s => {
      const qtd = parseFloat(s.quantidade)
      return s.descricao && !isNaN(qtd) && qtd > 0
    })
    if (valid.length === 0) {
      toast.error('Preencha a quantidade dos itens selecionados')
      return
    }
    addSuggestionsMutation.mutate(valid)
  }

  // Group suggestions by activity name
  const groupedSuggestions = useMemo(() => {
    const groups: { activityName: string; sinapiCodigo: string; items: SuggestionItem[] }[] = []
    const map = new Map<string, SuggestionItem[]>()
    const order: string[] = []
    for (const s of suggestions) {
      const key = `${s.activityName}__${s.sinapiCodigo}`
      if (!map.has(key)) { map.set(key, []); order.push(key) }
      map.get(key)!.push(s)
    }
    for (const key of order) {
      const items = map.get(key)!
      groups.push({ activityName: items[0].activityName, sinapiCodigo: items[0].sinapiCodigo, items })
    }
    return groups
  }, [suggestions])

  const hasSuggestions = suggestions.length > 0 || suggestionsLoading || childSinapiCodigos.length > 0

  // === AI Suggestions for activities without SINAPI ===
  interface AISuggestionItem {
    key: string
    selected: boolean
    activityName: string
    nome: string
    unidade: string
    coeficiente: string
    quantidade: string
    quantidadeIA: boolean // true = quantidade calculada pela IA, não recalcular com baseQuantity
    precoUnitario: string
    justificativa: string
  }

  const [aiSuggestions, setAiSuggestions] = useState<AISuggestionItem[]>([])
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false)
  const [aiSuggestionsError, setAiSuggestionsError] = useState<string | null>(null)
  const [aiSuggestionsExpanded, setAiSuggestionsExpanded] = useState(true)
  const [aiContext, setAiContext] = useState('')

  const handleSuggestMaterials = async () => {
    if (childrenWithoutSinapi.length === 0) return
    setAiSuggestionsLoading(true)
    setAiSuggestionsError(null)
    try {
      const activityNames = childrenWithoutSinapi.map((c: any) => c.name)
      const contextParts: string[] = []
      if (aiContext.trim()) contextParts.push(aiContext.trim())
      if (baseQuantity > 0) contextParts.push(`Área/metragem informada: ${baseQuantity}`)
      const result = await aiAPI.suggestMaterials(activityNames, contextParts.length > 0 ? contextParts.join('. ') : undefined)
      const items: AISuggestionItem[] = []
      for (const act of result.activities || []) {
        for (const mat of act.materials || []) {
          const coef = Number(mat.coeficiente) || 0
          const qtdIA = mat.quantidade != null ? Number(mat.quantidade) : null
          const hasQtdIA = qtdIA != null && qtdIA > 0
          items.push({
            key: `${act.activityName}_${mat.nome}_${mat.unidade}`,
            selected: false,
            activityName: act.activityName,
            nome: mat.nome,
            unidade: mat.unidade,
            coeficiente: String(coef),
            quantidade: hasQtdIA
              ? String(+qtdIA.toFixed(2))
              : baseQuantity > 0 ? String(+(coef * baseQuantity).toFixed(2)) : '',
            quantidadeIA: hasQtdIA,
            precoUnitario: '',
            justificativa: mat.justificativa || '',
          })
        }
      }
      setAiSuggestions(items)
      setAiSuggestionsExpanded(true)
    } catch (err: any) {
      setAiSuggestionsError(err?.message || 'Erro ao buscar sugestões da IA')
      toast.error('Erro ao buscar sugestões da IA')
    } finally {
      setAiSuggestionsLoading(false)
    }
  }

  // Recalculate AI suggestion quantities when baseQuantity changes (skip items with quantidadeIA)
  useEffect(() => {
    if (aiSuggestions.length === 0) return
    setAiSuggestions(prev => prev.map(s => {
      // Items with quantidade calculated by AI should not be recalculated
      if (s.quantidadeIA) return s
      const coef = parseFloat(s.coeficiente) || 0
      return {
        ...s,
        quantidade: baseQuantity > 0 ? String(+(coef * baseQuantity).toFixed(2)) : '',
      }
    }))
  }, [baseQuantity])

  const toggleAISuggestion = (key: string) => {
    setAiSuggestions(prev => prev.map(s => s.key === key ? { ...s, selected: !s.selected } : s))
  }

  const toggleAllAISuggestions = (selected: boolean) => {
    setAiSuggestions(prev => prev.map(s => ({ ...s, selected })))
  }

  const updateAISuggestionField = (key: string, field: 'coeficiente' | 'quantidade' | 'precoUnitario', value: string) => {
    setAiSuggestions(prev => prev.map(s => {
      if (s.key !== key) return s
      const updated = { ...s, [field]: value }
      // If coeficiente changed, recalculate quantidade
      if (field === 'coeficiente' && baseQuantity > 0) {
        const coef = parseFloat(value) || 0
        updated.quantidade = String(+(coef * baseQuantity).toFixed(2))
      }
      return updated
    }))
  }

  const selectedAISuggestions = aiSuggestions.filter(s => s.selected)

  const addAISuggestionsMutation = useMutation({
    mutationFn: async (items: AISuggestionItem[]) => {
      const batchItems = items
        .filter(item => {
          const qtd = parseFloat(item.quantidade)
          return item.nome && !isNaN(qtd) && qtd > 0
        })
        .map(item => ({
          nome: item.nome,
          unidade: item.unidade,
          quantidade: parseFloat(item.quantidade),
          precoUnitario: parseFloat(item.precoUnitario) || 0,
          projectActivityId: fixedActivityId || undefined,
          etapa: fixedActivityName || undefined,
          ambienteId: ambienteId || undefined,
        }))
      if (batchItems.length === 0) throw new Error('Nenhum item válido para adicionar')
      return levantamentoAPI.batchCreateItems(projectId, levantamentoId, batchItems)
    },
    onSuccess: (_res, items) => {
      toast.success(`${items.length} ite${items.length === 1 ? 'm adicionado' : 'ns adicionados'}`)
      queryClient.invalidateQueries({ queryKey: ['levantamento-project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['workflow-check', 'levantamento-items'] })
      const addedKeys = new Set(selectedAISuggestions.map(s => s.key))
      setAiSuggestions(prev => prev.map(s => addedKeys.has(s.key) ? { ...s, selected: false } : s))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleAddAISuggestions = () => {
    const valid = selectedAISuggestions.filter(s => {
      const qtd = parseFloat(s.quantidade)
      return s.nome && !isNaN(qtd) && qtd > 0
    })
    if (valid.length === 0) {
      toast.error('Preencha a quantidade dos itens selecionados')
      return
    }
    addAISuggestionsMutation.mutate(valid)
  }

  // Group AI suggestions by activity name
  const groupedAISuggestions = useMemo(() => {
    const groups: { activityName: string; items: AISuggestionItem[] }[] = []
    const map = new Map<string, AISuggestionItem[]>()
    const order: string[] = []
    for (const s of aiSuggestions) {
      if (!map.has(s.activityName)) { map.set(s.activityName, []); order.push(s.activityName) }
      map.get(s.activityName)!.push(s)
    }
    for (const name of order) {
      groups.push({ activityName: name, items: map.get(name)! })
    }
    return groups
  }, [aiSuggestions])

  return (
    <div className="space-y-4">
      {/* SINAPI Suggestions */}
      {hasSuggestions && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 overflow-hidden">
          <button
            type="button"
            className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-amber-50 transition-colors"
            onClick={() => setSuggestionsExpanded(e => !e)}
          >
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                Sugestões de materiais
              </span>
              {suggestions.length > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  {suggestions.length} ite{suggestions.length === 1 ? 'm' : 'ns'}
                </Badge>
              )}
            </div>
            {suggestionsExpanded
              ? <ChevronDown className="h-4 w-4 text-amber-500" />
              : <ChevronRight className="h-4 w-4 text-amber-500" />
            }
          </button>

          {suggestionsExpanded && (
            <div className="border-t border-amber-200 px-4 py-3 space-y-3">
              {suggestionsLoading ? (
                <div className="flex items-center gap-2 text-sm text-amber-600 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando sugestões na base SINAPI...
                </div>
              ) : suggestions.length === 0 && childSinapiCodigos.length > 0 ? (
                <p className="text-xs text-amber-600">Nenhum insumo encontrado para as composições vinculadas.</p>
              ) : (
                <>
                  {/* Select all / Deselect all */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-xs text-amber-700 underline hover:text-amber-900"
                        onClick={() => toggleAllSuggestions(true)}
                      >
                        Selecionar todos
                      </button>
                      <button
                        type="button"
                        className="text-xs text-amber-700 underline hover:text-amber-900"
                        onClick={() => toggleAllSuggestions(false)}
                      >
                        Limpar seleção
                      </button>
                    </div>
                    {selectedSuggestions.length > 0 && (
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleAddSuggestions}
                        disabled={addSuggestionsMutation.isPending}
                      >
                        {addSuggestionsMutation.isPending ? (
                          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                        ) : (
                          <PackagePlus className="h-3 w-3 mr-1.5" />
                        )}
                        Adicionar {selectedSuggestions.length} selecionado{selectedSuggestions.length !== 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>

                  {/* Grouped suggestions */}
                  {groupedSuggestions.map((group) => (
                    <div key={`${group.activityName}_${group.sinapiCodigo}`} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-neutral-700">{group.activityName}</span>
                        <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600">
                          SINAPI {group.sinapiCodigo}
                        </Badge>
                      </div>
                      <div className="rounded border border-amber-100 bg-white divide-y divide-amber-100">
                        {group.items.map((s) => (
                          <div key={s.key} className="flex items-center gap-2 px-3 py-1.5">
                            <Checkbox
                              checked={s.selected}
                              onChange={() => toggleSuggestion(s.key)}
                              className="h-3.5 w-3.5"
                            />
                            <span className={cn(
                              "flex-1 text-xs min-w-0 truncate",
                              s.selected ? 'text-neutral-800' : 'text-neutral-500'
                            )} title={s.descricao}>
                              {s.descricao}
                            </span>
                            <span className="text-[10px] text-neutral-400 w-8 text-center shrink-0">{s.unidade}</span>
                            <Input
                              type="number"
                              className="h-6 w-20 text-xs text-right shrink-0"
                              placeholder="Qtd"
                              value={s.quantidade}
                              onChange={(e) => updateSuggestionField(s.key, 'quantidade', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                            <Input
                              type="number"
                              className="h-6 w-24 text-xs text-right shrink-0"
                              placeholder="R$ Preço"
                              value={s.precoUnitario}
                              onChange={(e) => updateSuggestionField(s.key, 'precoUnitario', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Suggestions panel for activities without SINAPI */}
      {childrenWithoutSinapi.length > 0 && (
        <div className="rounded-lg border border-violet-200 bg-gradient-to-b from-violet-50/80 to-white overflow-hidden">
          <button
            type="button"
            className="flex items-center justify-between w-full px-4 py-3 hover:bg-violet-50/60 transition-colors"
            onClick={() => setAiSuggestionsExpanded(e => !e)}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-medium text-violet-800">
                Sugestões de materiais
              </span>
              <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-700 border border-violet-200">
                IA
              </Badge>
              {aiSuggestions.length > 0 && (
                <span className="text-xs text-violet-500">
                  {aiSuggestions.length} ite{aiSuggestions.length !== 1 ? 'ns' : 'm'}
                </span>
              )}
            </div>
            {aiSuggestionsExpanded
              ? <ChevronDown className="h-4 w-4 text-violet-500" />
              : <ChevronRight className="h-4 w-4 text-violet-500" />
            }
          </button>

          {aiSuggestionsExpanded && (
            <div className="border-t border-violet-200 px-4 py-3 space-y-3">
              {/* Activity tags */}
              <div className="flex flex-wrap gap-1.5">
                {childrenWithoutSinapi.map((child: any) => (
                  <span
                    key={child.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-violet-100 text-violet-700 border border-violet-200"
                  >
                    {child.name}
                  </span>
                ))}
              </div>

              {/* Context input + Suggest button */}
              {aiSuggestions.length === 0 && !aiSuggestionsLoading && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-violet-700">
                      Descreva o contexto da obra (opcional)
                    </label>
                    <textarea
                      className="w-full rounded-md border border-violet-200 bg-white px-3 py-2 text-xs text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 resize-none"
                      rows={2}
                      placeholder="Ex: Reforma de banheiro 3x2m, piso e parede até 1,50m de altura..."
                      value={aiContext}
                      onChange={(e) => setAiContext(e.target.value)}
                      maxLength={500}
                    />
                    <p className="text-[10px] text-violet-400">
                      Quanto mais detalhes, mais precisas serão as sugestões de materiais e quantidades.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="text-xs bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={handleSuggestMaterials}
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    Sugerir Materiais com IA
                  </Button>
                </div>
              )}

              {/* Loading */}
              {aiSuggestionsLoading && (
                <div className="flex items-center gap-2 text-sm text-violet-600 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analisando atividades e gerando sugestões de materiais...
                </div>
              )}

              {/* Error */}
              {aiSuggestionsError && (
                <div className="text-xs text-red-600 py-1">
                  {aiSuggestionsError}
                  <button type="button" className="ml-2 underline" onClick={handleSuggestMaterials}>
                    Tentar novamente
                  </button>
                </div>
              )}

              {/* AI Suggestions results */}
              {aiSuggestions.length > 0 && (
                <>
                  {/* Hint when baseQuantity = 0 */}
                  {baseQuantity === 0 && (
                    <p className="text-xs text-violet-500 italic">
                      Informe a metragem acima para calcular as quantidades automaticamente.
                    </p>
                  )}

                  {/* Select all / Clear / Add button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-xs text-violet-700 underline hover:text-violet-900"
                        onClick={() => toggleAllAISuggestions(true)}
                      >
                        Selecionar todos
                      </button>
                      <button
                        type="button"
                        className="text-xs text-violet-700 underline hover:text-violet-900"
                        onClick={() => toggleAllAISuggestions(false)}
                      >
                        Limpar seleção
                      </button>
                      <button
                        type="button"
                        className="text-xs text-violet-700 underline hover:text-violet-900"
                        onClick={handleSuggestMaterials}
                      >
                        Gerar novamente
                      </button>
                    </div>
                    {selectedAISuggestions.length > 0 && (
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
                        onClick={handleAddAISuggestions}
                        disabled={addAISuggestionsMutation.isPending}
                      >
                        {addAISuggestionsMutation.isPending ? (
                          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                        ) : (
                          <PackagePlus className="h-3 w-3 mr-1.5" />
                        )}
                        Adicionar {selectedAISuggestions.length} selecionado{selectedAISuggestions.length !== 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>

                  {/* Grouped suggestions by activity */}
                  {groupedAISuggestions.map((group) => (
                    <div key={group.activityName} className="space-y-1.5">
                      <span className="text-xs font-medium text-neutral-700">{group.activityName}</span>
                      <div className="rounded border border-violet-100 bg-white divide-y divide-violet-100">
                        {group.items.map((s) => (
                          <div key={s.key} className="flex items-center gap-2 px-3 py-1.5">
                            <Checkbox
                              checked={s.selected}
                              onChange={() => toggleAISuggestion(s.key)}
                              className="h-3.5 w-3.5"
                            />
                            <div className={cn(
                              "flex-1 min-w-0",
                              s.selected ? 'text-neutral-800' : 'text-neutral-500'
                            )}>
                              <span className="text-xs truncate block" title={s.nome}>{s.nome}</span>
                              {s.justificativa && (
                                <span className="text-[10px] text-neutral-400 truncate block" title={s.justificativa}>
                                  {s.justificativa}
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-neutral-400 w-8 text-center shrink-0">{s.unidade}</span>
                            <Input
                              type="number"
                              className="h-6 w-16 text-xs text-right shrink-0"
                              placeholder="Coef"
                              title="Coeficiente"
                              value={s.coeficiente}
                              onChange={(e) => updateAISuggestionField(s.key, 'coeficiente', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                            <Input
                              type="number"
                              className="h-6 w-20 text-xs text-right shrink-0"
                              placeholder="Qtd"
                              title="Quantidade"
                              value={s.quantidade}
                              onChange={(e) => updateAISuggestionField(s.key, 'quantidade', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                            <Input
                              type="number"
                              className="h-6 w-24 text-xs text-right shrink-0"
                              placeholder="R$ Preço"
                              title="Preço unitário"
                              value={s.precoUnitario}
                              onChange={(e) => updateAISuggestionField(s.key, 'precoUnitario', e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* View mode toggle */}
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={() => setViewMode('simple')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
            viewMode === 'simple'
              ? 'bg-neutral-900 text-white'
              : 'text-neutral-500 hover:bg-neutral-100',
          )}
        >
          <List className="h-3.5 w-3.5" />
          Simples
        </button>
        <button
          type="button"
          onClick={() => setViewMode('grouped')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
            viewMode === 'grouped'
              ? 'bg-neutral-900 text-white'
              : 'text-neutral-500 hover:bg-neutral-100',
          )}
        >
          <Layers className="h-3.5 w-3.5" />
          Completa
        </button>
        {hasComposicoes && (
          <button
            type="button"
            onClick={() => setViewMode('detailed')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              viewMode === 'detailed'
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:bg-neutral-100',
            )}
          >
            <GitBranch className="h-3.5 w-3.5" />
            Detalhada
          </button>
        )}
      </div>

      {/* Simple view — flat table */}
      {viewMode === 'simple' && (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs min-w-[200px]">Material / Serviço</TableHead>
                <TableHead className="text-xs w-20">Unidade</TableHead>
                <TableHead className="text-xs w-24 text-right">Qtd</TableHead>
                <TableHead className="text-xs w-28 text-right">Preço Unit.</TableHead>
                <TableHead className="text-xs w-28 text-right">Total</TableHead>
                {!fixedActivityId && <TableHead className="text-xs w-32">Etapa</TableHead>}
                <TableHead className="text-xs w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.map(renderItemRow)}
              {renderNewItemRow()}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Grouped view — items grouped by etapa with subtotals */}
      {viewMode === 'grouped' && (
        <div className="space-y-3">
          {groupedItens.map((group) => (
            <div key={group.key} className="border rounded-lg overflow-hidden">
              {/* Group header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-50 border-b">
                <div className="flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="text-sm font-semibold text-neutral-800">{group.label}</span>
                  <span className="text-xs text-neutral-400">
                    {group.items.length} ite{group.items.length === 1 ? 'm' : 'ns'}
                  </span>
                </div>
                <span className="text-sm font-bold text-green-700">{formatCurrency(group.total)}</span>
              </div>

              {/* Group items */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs min-w-[200px]">Material / Serviço</TableHead>
                    <TableHead className="text-xs w-20">Unidade</TableHead>
                    <TableHead className="text-xs w-24 text-right">Qtd</TableHead>
                    <TableHead className="text-xs w-28 text-right">Preço Unit.</TableHead>
                    <TableHead className="text-xs w-28 text-right">Total</TableHead>
                    <TableHead className="text-xs w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.items.map(renderItemRow)}
                </TableBody>
              </Table>
            </div>
          ))}

          {/* New item row (outside groups) */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableBody>
                {renderNewItemRow()}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Detailed view — items with expandable SINAPI composition trees */}
      {viewMode === 'detailed' && (
        <div className="space-y-3">
          {/* SINAPI params bar */}
          <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-xs font-medium text-blue-700">SINAPI:</span>
            <Select value={sinapiUf} onValueChange={(v) => { setSinapiUf(v); setTreeCache({}); setExpandedTreeId(null) }}>
              <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'].map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sinapiMes} onValueChange={(v) => { setSinapiMes(v); setTreeCache({}); setExpandedTreeId(null) }}>
              <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Mês ref..." /></SelectTrigger>
              <SelectContent>
                {(mesesDisponiveis || []).map((m: string) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {groupedItens.map((group) => (
            <div key={group.key} className="border rounded-lg overflow-hidden">
              {/* Group header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-50 border-b">
                <div className="flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="text-sm font-semibold text-neutral-800">{group.label}</span>
                  <span className="text-xs text-neutral-400">
                    {group.items.length} ite{group.items.length === 1 ? 'm' : 'ns'}
                  </span>
                </div>
                <span className="text-sm font-bold text-green-700">{formatCurrency(group.total)}</span>
              </div>

              {/* Items with expandable trees */}
              <div className="divide-y">
                {group.items.map((item: any) => {
                  const total = Number(item.quantidade) * Number(item.precoUnitario)
                  const hasTree = !!item.sinapiComposicaoId
                  const isExpanded = expandedTreeId === item.id
                  const isLoadingTree = treeLoading === item.sinapiComposicaoId
                  const treeData = hasTree ? treeCache[item.sinapiComposicaoId] : null

                  return (
                    <div key={item.id}>
                      <div
                        className={cn(
                          'flex items-center gap-3 px-4 py-2.5 text-xs',
                          hasTree ? 'cursor-pointer hover:bg-neutral-50' : '',
                        )}
                        onClick={() => hasTree && toggleTree(item.id, item.sinapiComposicaoId)}
                      >
                        {/* Expand icon */}
                        <div className="w-4 flex-shrink-0">
                          {hasTree && (
                            isLoadingTree && expandedTreeId === item.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />
                            ) : isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5 text-neutral-500" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
                            )
                          )}
                        </div>

                        {/* Item info */}
                        <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-neutral-800">{item.nome}</span>
                          {(item.sinapiInsumo?.codigo || item.sinapiInsumoId || hasTree) && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-blue-300 text-blue-600 font-mono">
                              SINAPI {item.sinapiInsumo?.codigo || ''}
                            </Badge>
                          )}
                        </div>
                        <span className="text-neutral-500 w-12 text-center flex-shrink-0">{item.unidade}</span>
                        <span className="font-mono text-neutral-600 w-16 text-right flex-shrink-0">{Number(item.quantidade).toFixed(2)}</span>
                        <span className="font-mono text-neutral-600 w-24 text-right flex-shrink-0">{formatCurrency(Number(item.precoUnitario))}</span>
                        <span className="font-mono font-medium text-neutral-800 w-24 text-right flex-shrink-0">{formatCurrency(total)}</span>
                        <div className="w-8 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (confirm('Remover este item?')) deleteMutation.mutate(item.id)
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded composition tree */}
                      {isExpanded && treeData && (
                        <div className="px-4 pb-3 pt-1 bg-neutral-50/50 border-t border-dashed border-neutral-200">
                          <ComposicaoTree data={treeData} />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Total bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 rounded-lg border">
        <span className="text-sm font-medium text-neutral-600">
          {itens.length} ite{itens.length === 1 ? 'm' : 'ns'}
        </span>
        <div className="text-right">
          <span className="text-sm text-neutral-500 mr-3">Total:</span>
          <span className="text-lg font-bold text-green-700">{formatCurrency(totalGeral)}</span>
        </div>
      </div>

      <p className="text-xs text-neutral-400">
        Dica: clique duas vezes em uma linha para editar.
      </p>
    </div>
  )
}
